/**
 * 문제 신고 창 — 화면 오류 수집 2단계 (2026-09-14, docs/features/16-ops-log.md F-16-12).
 *
 * 자동 수집은 "무엇이 났나" 를 알려 주지만 "무엇을 하려다 났나" 는 사용자만 안다. 시범 오픈은 사용자가
 * 적어서 신고 한 건의 값어치가 크다.
 *
 * ## 함께 싣는 것 — 사용자가 적지 않아도 흐름이 보이게
 *   · 이 창에서 난 오류의 **번호** — 서버 기록(detail.incidentId)과 이어진다. 최근 10분 것은 기본으로 고른다
 *   · 직전에 쓴 기능(화이트리스트 id)·빌드·화면·브라우저 — 무엇이 실리는지 창에 그대로 보여 준다
 *
 * ## 이미지는 사용자가 붙인 것만
 *   화면 자동 캡처를 들이지 않았다. 편집기·차트를 제대로 못 그리고, 무엇이 찍혔는지 사용자가 모른 채 나간다.
 *   붙여 넣기·끌어다 놓기·파일 선택으로 받고, 조회 결과가 찍혔으면 가리라고 알린다.
 *
 * ## 여는 곳은 셋 — 모두 openFeedback() 하나를 부른다
 *   도움말 › 문제 신고 · 에러 경계의 안내 화면 · 오류 알림(toast)이 뜬 뒤 잠시 나타나는 칩.
 *   칩은 toast.onChange 한 곳에서 띄운다 — 호출부마다 링크를 넣으면 반드시 빠뜨린다.
 *
 * 로그인한 사람만 보낼 수 있다(서버도 막는다). 스탠드얼론에는 두지 않는다 — 신고가 그 PC 에만 쌓인다.
 *
 * ## 답 — "내 신고" (2026-09-23, QBase 운영 감시 VOC)
 *   백엔드가 신고를 QBase 운영 감시로 넘기면 AI 가 분류해 먼저 답하고(질문이면 답, 결함 · 개선이면 검토 안내),
 *   담당자가 이어 답한다. 백엔드가 1분마다 대화를 받아 신고에 붙이고, 여기 "내 신고" 에서 본다.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { FiFlag, FiImage, FiLoader, FiX, FiCheckCircle, FiInbox } from 'react-icons/fi';
import AppModal from './common/AppModal';
import { fetchFeedback, getRecentActions, getRecentErrors, logAction, markFeedbackSeen, sendFeedback } from '../services/opsLog';
import useFeedbackReplies from '../hooks/useFeedbackReplies';
import './FeedbackDialog.css';

import { IS_STANDALONE } from '../config/features';
import { storedTime } from '../utils/storedTime';
/** 서버(`opsLog/router.js`)의 상한과 같다 — 화면이 먼저 알려 줘야 보내기 전에 고친다. */
const MAX_TEXT = 2000;
const MAX_IMAGES = 3;
const MAX_IMAGE_BYTES = 3 * 1024 * 1024;
const IMAGE_TYPES = ['image/png', 'image/jpeg', 'image/webp'];
/** 이 시간 안의 오류는 기본으로 고른다 — 방금 난 것을 신고하는 경우가 대부분이다. */
const RECENT_MS = 10 * 60 * 1000;
/** 오류 알림 뒤 칩이 떠 있는 시간. */
const CHIP_MS = 12000;

const objectUrl = (file) => (typeof URL !== 'undefined' && typeof URL.createObjectURL === 'function' ? URL.createObjectURL(file) : '');
const revokeUrl = (url) => { if (url && typeof URL.revokeObjectURL === 'function') URL.revokeObjectURL(url); };

let opener = null;

/** 훅을 못 쓰는 곳(에러 경계·메뉴 정의)에서 연다. Provider 가 없으면(스탠드얼론·테스트) 아무것도 하지 않는다. */
export function openFeedback(opts = {}) {
    if (opener) opener(opts || {});
}

/** 대화 상태 → 사용자에게 보일 말. 키는 i18n 검사가 찾을 수 있게 그대로 적는다 */
const STATUS_KEYS = {
    OPEN: 'assistant:opsLog.feedback.status.checking',
    ANALYZING: 'assistant:opsLog.feedback.status.checking',
    WAIT_APPR: 'assistant:opsLog.feedback.status.reviewing',
    FIX_APPROVED: 'assistant:opsLog.feedback.status.planned',
    REJECTED: 'assistant:opsLog.feedback.status.declined',
    RESOLVED: 'assistant:opsLog.feedback.status.done',
    BLOCKED_ENV: 'assistant:opsLog.feedback.status.checking',
    CONFIG_ERROR: 'assistant:opsLog.feedback.status.checking',
};
const FROM_KEYS = {
    USER: 'assistant:opsLog.feedback.from.me',
    AI: 'assistant:opsLog.feedback.from.ai',
    SYSTEM: 'assistant:opsLog.feedback.from.system',
    STAFF: 'assistant:opsLog.feedback.from.staff',
};

/** "20260923220000" 또는 "2026-09-23 13:00:00"(UTC) → 보기 좋은 모양. 모르는 모양은 그대로 */
function fmtAt(v) {
    const s = String(v || '');
    if (/^\d{14}$/.test(s)) return `${s.slice(4, 6)}-${s.slice(6, 8)} ${s.slice(8, 10)}:${s.slice(10, 12)}`;
    if (/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}/.test(s)) {
        const d = new Date(`${s.replace(' ', 'T')}Z`);
        if (!Number.isNaN(d.getTime())) {
            const p = (x) => String(x).padStart(2, '0');
            return `${p(d.getMonth() + 1)}-${p(d.getDate())} ${p(d.getHours())}:${p(d.getMinutes())}`;
        }
    }
    return s;
}

/** 내 신고 — 최근 20건과 각 신고의 대화. 열면 새 답 표시를 지운다 */
function MyReports() {
    const { t } = useTranslation();
    const [items, setItems] = useState(null);
    const [error, setError] = useState(null);

    useEffect(() => {
        let alive = true;
        fetchFeedback({ limit: 20 })
            .then((r) => { if (alive) setItems((r && r.items) || []); })
            .catch((e) => { if (alive) setError(String((e && e.message) || e)); });
        markFeedbackSeen();
        return () => { alive = false; };
    }, []);

    if (error) return <div className="fb-errorMsg" role="alert">{t('assistant:opsLog.feedback.mineError', { msg: error })}</div>;
    if (!items) return <div className="fb-hint">{t('assistant:opsLog.feedback.mineLoading')}</div>;
    if (!items.length) return <div className="fb-hint">{t('assistant:opsLog.feedback.mineEmpty')}</div>;
    return (
        <div className="fb-mine">
            {items.map((f) => {
                const conv = f.voc && f.voc.conversation;
                const statusKey = conv && STATUS_KEYS[conv.status];
                const msgs = ((conv && conv.messages) || []).filter((m, i) => !(i === 0 && m.from === 'USER'));
                return (
                    <div key={f.id} className={`fb-mineItem${f.voc && f.voc.unread ? ' fb-unread' : ''}`}>
                        <div className="fb-mineHead">
                            <span className="fb-ago">{fmtAt(f.createdAt)}</span>
                            <code>{f.id}</code>
                            <span className="fb-status">
                                {statusKey ? t(statusKey)
                                    : (f.voc && f.voc.sent ? t('assistant:opsLog.feedback.status.received') : t('assistant:opsLog.feedback.status.sending'))}
                            </span>
                        </div>
                        <div className="fb-mineText">{f.text || t('assistant:opsLog.feedback.noText')}</div>
                        {msgs.map(m => (
                            <div key={m.id} className={`fb-msg fb-msg-${String(m.from).toLowerCase()}`}>
                                <div className="fb-msgFrom">{t(FROM_KEYS[m.from] || FROM_KEYS.SYSTEM)} · {fmtAt(m.at)}</div>
                                <div className="fb-msgText">{m.text}</div>
                            </div>
                        ))}
                    </div>
                );
            })}
        </div>
    );
}

function agoText(at, t) {
    const min = Math.floor((Date.now() - at) / 60000);
    return min < 1 ? t('assistant:opsLog.feedback.justNow') : t('assistant:opsLog.feedback.minutesAgo', { n: min });
}

export function FeedbackDialog({ incidentId, initialTab, onClose }) {
    const { t } = useTranslation();
    const [tab, setTab] = useState(initialTab === 'mine' ? 'mine' : 'new');
    const isAuthenticated = useSelector(s => !!(s.auth && s.auth.isAuthenticated));
    // 새 답 수 — 도움말 메뉴의 숫자와 같은 출처. "내 신고" 를 열면(markFeedbackSeen) 함께 0 이 된다
    const replies = useFeedbackReplies(isAuthenticated && !IS_STANDALONE);
    // 창을 연 순간의 것 — 쓰는 동안 목록이 바뀌면 고른 것이 흔들린다
    const errors = useMemo(() => getRecentErrors().slice().reverse(), []);
    const actions = useMemo(() => getRecentActions(), []);
    const [text, setText] = useState('');
    const [picked, setPicked] = useState(() => new Set(
        errors.filter(e => e.id === incidentId || Date.now() - e.at < RECENT_MS).map(e => e.id),
    ));
    const [images, setImages] = useState([]);
    const [sending, setSending] = useState(false);
    const [error, setError] = useState(null);
    const [receipt, setReceipt] = useState(null);
    const fileRef = useRef(null);
    const imagesRef = useRef(images);
    imagesRef.current = images;

    useEffect(() => { logAction('modal.feedback'); }, []);
    // 미리보기 주소는 창을 닫을 때 풀어 준다
    useEffect(() => () => imagesRef.current.forEach(im => revokeUrl(im.url)), []);

    const addFiles = useCallback((files) => {
        const list = Array.from(files || []).filter(f => f && f.type && f.type.startsWith('image/'));
        if (!list.length) return;
        const next = [...imagesRef.current];
        for (const f of list) {
            if (next.length >= MAX_IMAGES) {
                toast.warning(t('assistant:opsLog.feedback.tooMany', { max: MAX_IMAGES }));
                break;
            }
            if (!IMAGE_TYPES.includes(f.type) || f.size > MAX_IMAGE_BYTES) {
                toast.warning(t('assistant:opsLog.feedback.imageRejected', { name: f.name || 'image' }));
                continue;
            }
            next.push({ file: f, url: objectUrl(f) });
        }
        setImages(next);
    }, [t]);

    const removeImage = (i) => {
        revokeUrl(images[i] && images[i].url);
        setImages(images.filter((_x, j) => j !== i));
    };

    const togglePick = (id) => setPicked((prev) => {
        const next = new Set(prev);
        if (next.has(id)) next.delete(id); else next.add(id);
        return next;
    });

    // 캡처는 대개 클립보드에 있다 — 글자를 붙여 넣을 때는 건드리지 않는다
    const onPaste = (e) => {
        const files = e.clipboardData && e.clipboardData.files;
        if (files && files.length) { e.preventDefault(); addFiles(files); }
    };
    const onDrop = (e) => {
        if (e.dataTransfer && e.dataTransfer.files && e.dataTransfer.files.length) { e.preventDefault(); addFiles(e.dataTransfer.files); }
    };

    const canSend = isAuthenticated && !sending && !!(text.trim() || picked.size || images.length);

    const submit = async () => {
        if (!canSend) return;
        setSending(true);
        setError(null);
        try {
            const r = await sendFeedback({ text: text.trim(), incidentIds: [...picked], images: images.map(im => im.file) });
            setReceipt(r.id);
            setText('');
            setPicked(new Set());
            imagesRef.current.forEach(im => revokeUrl(im.url));
            setImages([]);
        } catch (err) {
            // 실패는 창에 남긴다 — 토스트로 흘리면 쓴 글이 어디 갔는지 모른다
            const code = err && err.errorType;
            setError(code ? t(`common:errors.${code}`, { defaultValue: err.message }) : String((err && err.message) || err));
        } finally { setSending(false); }
    };

    return (
        <AppModal open onClose={onClose} title={t('assistant:opsLog.feedback.title')} icon={<FiFlag size={14} />} className="fb-modal">
            {isAuthenticated ? (
                <div className="fb-tabs" role="tablist">
                    <button type="button" role="tab" aria-selected={tab === 'new'} className={`fb-tab${tab === 'new' ? ' fb-tabOn' : ''}`}
                        onClick={() => { setTab('new'); setReceipt(null); }}>
                        <FiFlag size={11} /> {t('assistant:opsLog.feedback.tabNew')}
                    </button>
                    <button type="button" role="tab" aria-selected={tab === 'mine'} className={`fb-tab${tab === 'mine' ? ' fb-tabOn' : ''}`}
                        onClick={() => setTab('mine')}>
                        <FiInbox size={11} /> {t('assistant:opsLog.feedback.tabMine')}
                        {replies > 0 ? (
                            <span className="fb-tabBadge" title={t('menu:help.reportReplies', { n: replies })}>{replies}</span>
                        ) : null}
                    </button>
                </div>
            ) : null}
            {tab === 'mine' ? (
                <div className="fb-body"><MyReports /></div>
            ) : receipt ? (
                <div className="fb-body fb-done">
                    <FiCheckCircle size={22} />
                    <div className="fb-doneTitle">{t('assistant:opsLog.feedback.sent')}</div>
                    <div className="fb-receipt">{t('assistant:opsLog.feedback.receipt', { id: receipt })}</div>
                    <div className="fb-hint">{t('assistant:opsLog.feedback.replyWhere')}</div>
                    <div className="fb-actions">
                        <button type="button" className="app-btn" onClick={() => setTab('mine')}>{t('assistant:opsLog.feedback.viewMine')}</button>
                        <button type="button" className="app-btn app-btn-primary" onClick={onClose}>{t('common:ui.close')}</button>
                    </div>
                </div>
            ) : (
                <div className="fb-body" onPaste={onPaste} onDragOver={(e) => e.preventDefault()} onDrop={onDrop} role="presentation">
                    {!isAuthenticated ? <div className="fb-notice">{t('assistant:opsLog.feedback.needLogin')}</div> : null}

                    <label className="fb-label" htmlFor="fb-text">{t('assistant:opsLog.feedback.what')}</label>
                    <textarea
                        id="fb-text" className="fb-text" rows={5} value={text} maxLength={MAX_TEXT}
                        placeholder={t('assistant:opsLog.feedback.placeholder')}
                        onChange={(e) => setText(e.target.value)}
                    />

                    <div className="fb-section">
                        <div className="fb-label">{t('assistant:opsLog.feedback.recentErrors')}</div>
                        {errors.length ? errors.map(e => (
                            <label key={e.id} className="fb-errorRow">
                                <input type="checkbox" checked={picked.has(e.id)} onChange={() => togglePick(e.id)} />
                                <code>{e.id}</code>
                                <span>{e.errorType}</span>
                                <span className="fb-ago">{agoText(e.at, t)}</span>
                            </label>
                        )) : <div className="fb-hint">{t('assistant:opsLog.feedback.noRecentErrors')}</div>}
                    </div>

                    <div className="fb-section">
                        <div className="fb-imagesHead">
                            <button
                                type="button" className="app-btn" disabled={images.length >= MAX_IMAGES}
                                onClick={() => fileRef.current && fileRef.current.click()}
                            >
                                <FiImage size={12} /> {t('assistant:opsLog.feedback.attach')}
                            </button>
                            <span className="fb-hint">
                                {t('assistant:opsLog.feedback.attachHint', { max: MAX_IMAGES, mb: MAX_IMAGE_BYTES / 1024 / 1024 })}
                            </span>
                            <input
                                ref={fileRef} type="file" accept={IMAGE_TYPES.join(',')} multiple hidden
                                onChange={(e) => { addFiles(e.target.files); e.target.value = ''; }}
                            />
                        </div>
                        {images.length ? (
                            <div className="fb-images">
                                {images.map((im, i) => (
                                    <div key={im.url || `${im.file.name}-${i}`} className="fb-thumb">
                                        {im.url ? <img src={im.url} alt={im.file.name} /> : <span>{im.file.name}</span>}
                                        <button
                                            type="button" className="fb-thumbX" onClick={() => removeImage(i)}
                                            title={t('assistant:opsLog.feedback.remove')} aria-label={t('assistant:opsLog.feedback.remove')}
                                        >
                                            <FiX size={11} />
                                        </button>
                                    </div>
                                ))}
                            </div>
                        ) : null}
                        <div className="fb-privacy">{t('assistant:opsLog.feedback.privacy')}</div>
                        <div className="fb-hint">{t('assistant:opsLog.feedback.aiNotice')}</div>
                    </div>

                    <details className="fb-section fb-sends">
                        <summary>{t('assistant:opsLog.feedback.sendsWith')}</summary>
                        <ul>
                            <li>{t('assistant:opsLog.feedback.sendsActions', { count: actions.length })}</li>
                            <li>{t('assistant:opsLog.feedback.sendsContext')}</li>
                        </ul>
                        {actions.length ? (
                            <pre className="fb-actionList">{actions.map(a => `${storedTime(a.at, 'time')}  ${a.action}`).join('\n')}</pre>
                        ) : null}
                    </details>

                    {error ? <div className="fb-errorMsg" role="alert">{error}</div> : null}

                    <div className="fb-actions">
                        <button type="button" className="app-btn" onClick={onClose}>{t('common:ui.cancel')}</button>
                        <button type="button" className="app-btn app-btn-primary" onClick={submit} disabled={!canSend}>
                            {sending ? <FiLoader size={12} className="fb-spin" /> : <FiFlag size={12} />}
                            {sending ? t('assistant:opsLog.feedback.sending') : t('assistant:opsLog.feedback.send')}
                        </button>
                    </div>
                </div>
            )}
        </AppModal>
    );
}

FeedbackDialog.propTypes = {
    /** 이 번호의 오류를 골라 둔 채 연다(에러 경계에서) */
    incidentId: PropTypes.string,
    /** 'mine' 이면 "내 신고" 로 연다(새 답 알림에서) */
    initialTab: PropTypes.oneOf(['new', 'mine']),
    onClose: PropTypes.func.isRequired,
};
FeedbackDialog.defaultProps = { incidentId: null, initialTab: 'new' };

/** 신고 창과 칩을 앱 전체에 한 번 둔다. index.js 에서 **에러 경계 바깥**에 — 화면이 깨진 뒤에도 떠야 한다. */
export function FeedbackProvider({ children }) {
    const { t } = useTranslation();
    const isAuthenticated = useSelector(s => !!(s.auth && s.auth.isAuthenticated));
    const [request, setRequest] = useState(null);
    const [chip, setChip] = useState(false);

    useEffect(() => {
        if (IS_STANDALONE) return undefined;
        opener = (opts) => { setChip(false); setRequest({ incidentId: opts.incidentId || null, tab: opts.tab || 'new' }); };
        return () => { opener = null; };
    }, []);

    // 오류 알림이 뜨면 잠시 칩을 띄운다 — 로그인 전에는 띄우지 않는다(보낼 수 없다)
    useEffect(() => {
        if (IS_STANDALONE || !isAuthenticated || typeof toast.onChange !== 'function') return undefined;
        let timer = null;
        const off = toast.onChange((item) => {
            if (item && item.status === 'added' && item.type === 'error') {
                setChip(true);
                clearTimeout(timer);
                timer = setTimeout(() => setChip(false), CHIP_MS);
            }
        });
        return () => { clearTimeout(timer); if (typeof off === 'function') off(); };
    }, [isAuthenticated]);

    return (
        <>
            {children}
            {chip && !request ? (
                <button type="button" className="fb-chip" onClick={() => openFeedback()}>
                    <FiFlag size={12} /> {t('assistant:opsLog.feedback.chip')}
                </button>
            ) : null}
            {request ? <FeedbackDialog incidentId={request.incidentId} initialTab={request.tab} onClose={() => setRequest(null)} /> : null}
        </>
    );
}

FeedbackProvider.propTypes = { children: PropTypes.node };
FeedbackProvider.defaultProps = { children: null };

export default FeedbackDialog;
